import React, { useState } from 'react';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronRight, TrendingUp, Clock, Zap, PiggyBank, ArrowRight, X as CloseIcon, BarChart, Target } from 'lucide-react';
import { sendContactForm } from '../services/emailService';

const PredictiveAnalyticsPage = () => {
  // States for form and modal
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' }); // Track submission status
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    inquiryType: 'get-started' // Default inquiry type
  });

  // Navigation to home page
  const navigateToHome = () => {
    window.location.href = '/';
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: '',
      inquiryType: 'get-started'
    });
  };

  // Open modal with specific inquiry type
  const openModal = (type) => {
    setFormData(prev => ({...prev, inquiryType: type}));
    setShowModal(true);
    // Reset status when opening modal
    setSubmitStatus({ success: null, message: '' });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Send form data via EmailJS
    sendContactForm(formData)
      .then((response) => {
        console.log('Email sent successfully!', response);
        setSubmitStatus({
          success: true,
          message: 'Thanks for your interest! We will contact you soon.'
        });
        // Clear form but don't close modal immediately to show success message
        resetForm();
        // Close modal after delay to show success message
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus({ success: null, message: '' });
        }, 3000);
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setSubmitStatus({
          success: false,
          message: 'We could not send this automatically. Please email us at jacobvinoo@gmail.com with the details above.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const forecastData = [
    { month: 'Jan', actualRevenue: 42, forecastRevenue: 43, demandPressure: 58 },
    { month: 'Feb', actualRevenue: 38, forecastRevenue: 40, demandPressure: 54 },
    { month: 'Mar', actualRevenue: 47, forecastRevenue: 45, demandPressure: 66 },
    { month: 'Apr', actualRevenue: 44, forecastRevenue: 46, demandPressure: 61 },
    { month: 'May', actualRevenue: 53, forecastRevenue: 51, demandPressure: 73 },
    { month: 'Jun', actualRevenue: 49, forecastRevenue: 52, demandPressure: 67 },
    { month: 'Jul', actualRevenue: 57, forecastRevenue: 56, demandPressure: 79 },
    { month: 'Aug', actualRevenue: null, forecastRevenue: 54, demandPressure: 72 },
    { month: 'Sep', actualRevenue: null, forecastRevenue: 61, demandPressure: 84 },
    { month: 'Oct', actualRevenue: null, forecastRevenue: 58, demandPressure: 76 },
    { month: 'Nov', actualRevenue: null, forecastRevenue: 66, demandPressure: 89 },
    { month: 'Dec', actualRevenue: null, forecastRevenue: 62, demandPressure: 81 },
  ];

  const forecastSummary = [
    { label: 'Forecast range', value: '$54k-$66k', detail: 'expected monthly revenue', color: 'text-emerald-200' },
    { label: 'Confidence', value: '86%', detail: 'allows for seasonal variance', color: 'text-cyan-200' },
    { label: 'Risk windows', value: '3', detail: 'months needing earlier action', color: 'text-pink-200' },
  ];

  const ForecastTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null;
    }

    return (
      <div className="rounded-lg border border-gray-700 bg-gray-950/95 p-3 shadow-xl">
        <p className="mb-2 font-semibold text-white">{label}</p>
        {payload.map((entry) => {
          if (entry.value == null) {
            return null;
          }

          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-6 text-sm">
              <span className="text-gray-300">{entry.name}</span>
              <span className="font-semibold" style={{ color: entry.color }}>
                {entry.dataKey === 'demandPressure' ? `${entry.value}%` : `$${entry.value}k`}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-6 py-4 lg:px-12 bg-gray-900">
        <div className="flex items-center cursor-pointer" onClick={navigateToHome}>
          <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">Mosyni AI</span>
        </div>
        <div className="hidden md:flex space-x-8">
          <a href="/#services" className="hover:text-blue-400 transition">Services</a>
          <a href="/#benefits" className="hover:text-blue-400 transition">Benefits</a>
          <a href="/#methodology" className="hover:text-blue-400 transition">Methodology</a>
          <a href="/#contact" className="hover:text-blue-400 transition">Contact</a>
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-full font-semibold transition" onClick={navigateToHome}>
          Back to Home
        </button>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-pink-900 to-gray-900">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
              Predictive Analytics Solutions
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Anticipate market trends and customer behavior with AI-powered forecasting. Our predictive analytics helps small businesses gain foresight, minimize risks, and capitalize on future opportunities.
            </p>
            <button
              onClick={() => openModal('get-started')}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-full font-semibold text-lg transition flex items-center"
            >
              Get Started
              <ChevronRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">How Our Predictive Analytics Transform Your Business</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-blue-600 rounded-lg inline-block mb-4">
              <TrendingUp size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Future Forecasting</h3>
            <p className="text-gray-300">
              Predict sales trends, customer demand, and market shifts with advanced machine learning models that learn from your historical data and industry patterns.
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-purple-600 rounded-lg inline-block mb-4">
              <BarChart size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Customer Behavior Analysis</h3>
            <p className="text-gray-300">
              Understand customer purchase patterns, identify churn risks, and discover upsell opportunities with AI that predicts individual customer actions.
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-8 hover:shadow-lg transition">
            <div className="p-3 bg-pink-600 rounded-lg inline-block mb-4">
              <Target size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3">Risk Assessment</h3>
            <p className="text-gray-300">
              Identify potential risks in your business operations, supply chain, and market position with probability models that highlight vulnerabilities before they impact you.
            </p>
          </div>
        </div>
      </div>

      {/* Sample Visualization */}
      <div className="bg-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">See Into the Future</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our predictive models provide accurate forecasts that help you plan inventory, staffing, and marketing with confidence.
            </p>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-950 shadow-2xl shadow-black/30">
            <div className="grid grid-cols-1 border-b border-gray-800 md:grid-cols-3">
              {forecastSummary.map((item) => (
                <div key={item.label} className="border-b border-gray-800 p-5 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{item.label}</p>
                  <p className={`mt-2 text-2xl font-bold ${item.color}`}>{item.value}</p>
                  <p className="mt-1 text-sm text-gray-400">{item.detail}</p>
                </div>
              ))}
            </div>
            <div className="p-4 md:p-8">
              <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-xl font-bold">Revenue Forecast and Demand Pressure</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    Show actual performance, expected revenue, and the operating pressure that needs attention.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-gray-300">
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-blue-500"></span>Actual revenue</span>
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-300"></span>Forecast</span>
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-pink-500/70"></span>Demand pressure</span>
                </div>
              </div>
              <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={forecastData} margin={{ top: 16, right: 34, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 12 }} tickLine={false} axisLine={{ stroke: '#475569' }} />
                  <YAxis yAxisId="revenue" stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}k`} label={{ value: 'Revenue', angle: -90, position: 'insideLeft', fill: '#cbd5e1', fontSize: 12 }} />
                  <YAxis yAxisId="pressure" orientation="right" domain={[50, 100]} stroke="#f9a8d4" tick={{ fill: '#fbcfe8', fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(value) => `${value}%`} label={{ value: 'Demand pressure', angle: 90, position: 'insideRight', fill: '#fbcfe8', fontSize: 12 }} />
                  <Tooltip content={<ForecastTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
                  <Bar yAxisId="revenue" dataKey="actualRevenue" fill="#3b82f6" name="Actual revenue" radius={[6, 6, 0, 0]} />
                  <Area yAxisId="pressure" type="monotone" dataKey="demandPressure" fill="#ec4899" fillOpacity={0.18} stroke="#f472b6" name="Demand pressure" strokeWidth={2} />
                  <Line yAxisId="revenue" type="monotone" dataKey="forecastRevenue" stroke="#6ee7b7" name="Forecast revenue" strokeWidth={3} strokeDasharray="6 4" dot={{ r: 4, fill: '#6ee7b7', stroke: '#0f172a', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-4">
                <Target className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                <p className="text-sm text-emerald-50">
                  Forecasting turns a sales trend into next steps for stock, staffing, marketing, and cash flow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Benefits of Predictive Analytics</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Gain the competitive advantage that comes with knowing what's likely to happen before it does.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <Clock size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Proactive Decision Making</h3>
              <p className="text-gray-300">
                Move from reactive to proactive business management with early insights into market changes, customer needs, and operational challenges before they impact your business.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <PiggyBank size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Optimized Resource Allocation</h3>
              <p className="text-gray-300">
                Reduce waste by up to 30% by allocating inventory, staffing, and marketing resources based on predicted demand rather than guesswork.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <Zap size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Increased Revenue</h3>
              <p className="text-gray-300">
                Our clients typically see a 15-25% increase in sales through better targeting, personalized marketing, and optimized pricing strategies informed by predictive insights.
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="mr-6">
              <div className="p-3 bg-blue-600 rounded-full">
                <TrendingUp size={24} />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Risk Mitigation</h3>
              <p className="text-gray-300">
                Reduce business risks by identifying potential threats in advance, allowing you to develop contingency plans and avoid costly disruptions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to See the Future of Your Business?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Our data scientists will help you implement predictive analytics that deliver actionable insights for your specific business needs.
          </p>
          <button
            onClick={() => openModal('consultation')}
            className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold text-lg transition flex items-center mx-auto"
          >
            Schedule a Free Consultation
            <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">Do I need a large amount of data to use predictive analytics?</h3>
            <p className="text-gray-300">
              While more data generally leads to better predictions, our models can work effectively with smaller datasets common in small businesses. We can also leverage industry benchmarks and market data to enhance your predictions when internal data is limited.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">How accurate are the predictions?</h3>
            <p className="text-gray-300">
              Our models typically achieve 80-90% accuracy for short-term predictions, with accuracy decreasing for longer time horizons. We provide confidence intervals with all predictions and continuously refine models as new data becomes available.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">What types of predictions can you make for my business?</h3>
            <p className="text-gray-300">
              We can develop models for sales forecasting, customer churn prediction, inventory optimization, price sensitivity, customer lifetime value, marketing campaign effectiveness, and many other business metrics specific to your industry and goals.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">How long does it take to implement predictive analytics?</h3>
            <p className="text-gray-300">
              Basic models can be implemented in 3-4 weeks, while more complex predictive systems may take 2-3 months. We follow an iterative approach, delivering initial insights quickly and then refining and expanding the models over time.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-md relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              disabled={isSubmitting}
            >
              <CloseIcon size={24} />
            </button>

            <div className="p-6">
              <h3 className="text-2xl font-bold mb-6 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
                Get Started with Mosyni AI
                {formData.inquiryType === 'consultation' && " - Consultation Request"}
              </h3>

              {submitStatus.success === null ? (
                <form onSubmit={handleSubmit}>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website || ''}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-1">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-1">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-1">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-1">How can we help you?</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.inquiryType === 'consultation' ?
                          formData.message || "I'm interested in scheduling a consultation about Predictive Analytics solutions." :
                          formData.message}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      ></textarea>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-md transition-all duration-200"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Submit'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className={`p-4 rounded-md mb-4 ${submitStatus.success ? 'bg-green-700 bg-opacity-30' : 'bg-red-700 bg-opacity-30'}`}>
                  <p className="text-center">
                    {submitStatus.message}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictiveAnalyticsPage;
